import { Search } from "@mui/icons-material";
import {
  Box,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  INDUSTRY_DISPLAY_NAMES,
  PARTNER_TYPE_DISPLAY_NAMES,
  type IndustryType,
  type PartnerFilters,
  type PartnerType,
} from "types/partners";

interface ExploreFiltersProps {
  filters: PartnerFilters;
  onFiltersChange: (filters: PartnerFilters) => void;
  onClearFilters?: () => void;
  columnLayout?: boolean;
}

const selectSx = {
  height: "40px",
  "& .MuiSelect-select": { display: "flex", alignItems: "center" },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: (theme: { palette: { common: { black: string } } }) =>
      alpha(theme.palette.common.black, 0.23),
  },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
  },
};

export default function ExploreFilters({
  filters,
  onFiltersChange,
  onClearFilters,
  columnLayout = false,
}: ExploreFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search || "");
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onFiltersChange({ ...filters, search: value });
    }, 300);
  };

  // Sync local state with filters when filters change externally (e.g., clear)
  if (filters.search !== searchInput && !debounceRef.current) {
    setSearchInput(filters.search || "");
  }

  const handleIndustryChange = (value: string) => {
    onFiltersChange({ ...filters, industry_type: value as IndustryType });
  };

  const handlePartnerTypeChange = (value: string) => {
    if (value === "featured") {
      onFiltersChange({ ...filters, partner_type: "", featured: true });
    } else {
      onFiltersChange({
        ...filters,
        partner_type: value as PartnerType,
        featured: false,
      });
    }
  };

  const handleSortChange = (value: string) => {
    onFiltersChange({ ...filters, sort: value as PartnerFilters["sort"] });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      search: "",
      partner_type: "",
      industry_type: "",
      featured: false,
      sort: "name",
    });
    onClearFilters?.();
  };

  const hasActiveFilters =
    filters.search ||
    filters.partner_type ||
    filters.industry_type ||
    filters.featured;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: columnLayout ? "column" : "row",
        alignItems: columnLayout ? "stretch" : "center",
        gap: columnLayout ? 2 : { xs: 1.5, sm: 2, md: 3, lg: 4 },
        py: 1,
      }}
    >
      {/* Left container: Search + Filters + Clear — auto-scales */}
      <Box
        sx={{
          display: "flex",
          flexDirection: columnLayout ? "column" : "row",
          gap: columnLayout ? 2 : 1.5,
          alignItems: columnLayout ? "stretch" : "center",
          flex: columnLayout ? "none" : 1,
          minWidth: columnLayout ? "auto" : 0,
        }}
      >
        <TextField
          placeholder="Search partners"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{
            flex: columnLayout ? "none" : 1.5,
            minWidth: columnLayout ? "100%" : 120,
            maxWidth: columnLayout ? "100%" : "370px",
            "& .MuiInputBase-root": { height: "40px" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl
          size="small"
          sx={{
            flex: columnLayout ? "none" : 1,
            minWidth: columnLayout ? "100%" : 110,
            maxWidth: columnLayout ? "100%" : "170px",
          }}
        >
          <Select
            value={filters.industry_type || ""}
            onChange={(e) => handleIndustryChange(e.target.value)}
            displayEmpty
            renderValue={(val) =>
              val
                ? INDUSTRY_DISPLAY_NAMES[val as IndustryType]
                : "All Industries"
            }
            sx={selectSx}
          >
            <MenuItem value="">All Industries</MenuItem>
            {Object.entries(INDUSTRY_DISPLAY_NAMES).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{
            flex: columnLayout ? "none" : 1,
            minWidth: columnLayout ? "100%" : 130,
            maxWidth: columnLayout ? "100%" : "170px",
          }}
        >
          <Select
            value={filters.featured ? "featured" : filters.partner_type || ""}
            onChange={(e) => handlePartnerTypeChange(e.target.value)}
            displayEmpty
            renderValue={(val) =>
              val === "featured"
                ? "Featured"
                : val
                  ? PARTNER_TYPE_DISPLAY_NAMES[val as PartnerType]
                  : "All Partner Types"
            }
            sx={selectSx}
          >
            <MenuItem value="">All Partner Types</MenuItem>
            <MenuItem value="featured">Featured</MenuItem>
            {Object.entries(PARTNER_TYPE_DISPLAY_NAMES).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography
          component="button"
          onClick={hasActiveFilters ? handleClearFilters : undefined}
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: hasActiveFilters ? "primary.main" : "text.disabled",
            cursor: hasActiveFilters ? "pointer" : "default",
            background: "none",
            border: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
            p: 0,
            transition: "color 0.2s ease",
            alignSelf: columnLayout ? "flex-start" : "auto",
            "&:hover": {
              color: hasActiveFilters ? "primary.dark" : "text.disabled",
            },
          }}
        >
          Clear Filters
        </Typography>
      </Box>

      {/* Right container: Sort — fixed width */}
      <Box
        sx={{
          display: "flex",
          flexDirection: columnLayout ? "column" : "row",
          alignItems: columnLayout ? "stretch" : "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
        >
          Sort by:
        </Typography>
        <FormControl
          size="small"
          sx={{ minWidth: columnLayout ? "100%" : 130 }}
        >
          <Select
            value={filters.sort || "name"}
            onChange={(e) => handleSortChange(e.target.value)}
            displayEmpty
            sx={selectSx}
          >
            <MenuItem value="name">Name (A-Z)</MenuItem>
            <MenuItem value="-name">Name (Z-A)</MenuItem>
            <MenuItem value="created_at">Newest</MenuItem>
            <MenuItem value="-created_at">Oldest</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
